import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useTeams } from "@/contexts/teams-provider";
import { useAdminTeamsEmitter } from "@/hooks/useAdminTeamsEvents";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { AdminTeamDTO } from "@shaarp/common";
import { SlidersHorizontalIcon } from "lucide-react";
import { useState } from "react";
import { useToast, useToastError } from "../ui/use-toast";

type FeatureFlagKey =
  | "featureContactDirectEnabled"
  | "featureContactRevealEmailEnabled"
  | "featureContactRevealPhoneEnabled"
  | "featureReportingWeeklyActivityEnabled"
  | "featureNewNavigationEnabled";

const CONTACT_FLAG: FeatureFlagKey = "featureContactDirectEnabled";

type FeatureFlagItem = {
  key: FeatureFlagKey;
  label: string;
  // Set on a flag that hangs under contact: it cannot be on while contact is off.
  dependsOnContact?: boolean;
};

export default function DataTableCellAdminsFeatureFlags({
  team,
}: {
  team: AdminTeamDTO;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { refreshTeams } = useAdminTeamsEmitter();
  const { reloadMyTeams } = useTeams();

  const [open, setOpen] = useState(false);
  const [flags, setFlags] = useState<Record<FeatureFlagKey, boolean>>({
    featureContactDirectEnabled: team.featureContactDirectEnabled,
    featureContactRevealEmailEnabled: team.featureContactRevealEmailEnabled,
    featureContactRevealPhoneEnabled: team.featureContactRevealPhoneEnabled,
    featureReportingWeeklyActivityEnabled:
      team.featureReportingWeeklyActivityEnabled,
    featureNewNavigationEnabled: team.featureNewNavigationEnabled,
  });
  const [pendingFlag, setPendingFlag] = useState<FeatureFlagKey | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const enabledCount = Object.values(flags).filter(Boolean).length;
  const totalCount = Object.keys(flags).length;

  const updateFlag = async (flag: FeatureFlagKey, value: boolean) => {
    setPendingFlag(flag);
    try {
      await api.post(`/admin/team/${team.id}/feature-flags`, {
        [flag]: value,
      });
      // Cutting contact closes its two channels on the server too.
      const isContactCut = flag === CONTACT_FLAG && !value;
      setFlags((prev) => ({
        ...prev,
        [flag]: value,
        ...(isContactCut && {
          featureContactRevealEmailEnabled: false,
          featureContactRevealPhoneEnabled: false,
        }),
      }));
      setHasChanges(true);
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.featureFlags.toast.title"),
        description: t(
          "admin.dataTableAdminTeams.featureFlags.toast.description"
        ),
      });
    } catch {
      toastError();
    } finally {
      setPendingFlag(null);
    }
  };

  // Refreshing the list blanks the table while it loads, which unmounts this
  // cell and would close the dialog: refresh once, when it closes. The admin's
  // own teams are reloaded too, so a flag of the selected team shows at once.
  const handleOpenChange = (isOpen: boolean) => {
    // Held open until a change in flight lands, or its refresh would be lost.
    if (!isOpen && pendingFlag) return;
    setOpen(isOpen);
    if (!isOpen && hasChanges) {
      setHasChanges(false);
      refreshTeams();
      void reloadMyTeams();
    }
  };

  // Email and phone hang under contact (#757, #840): contact hides the key
  // contacts of a company, email and phone only hide a person's contact
  // details, so they cannot be on while contact is off.
  const contactItem: FeatureFlagItem = {
    key: CONTACT_FLAG,
    label: t("admin.dataTableAdminTeams.featureFlags.contactDirect"),
  };
  const contactChannelItems: FeatureFlagItem[] = [
    {
      key: "featureContactRevealEmailEnabled",
      label: t("admin.dataTableAdminTeams.featureFlags.emailReveal"),
      dependsOnContact: true,
    },
    {
      key: "featureContactRevealPhoneEnabled",
      label: t("admin.dataTableAdminTeams.featureFlags.phoneReveal"),
      dependsOnContact: true,
    },
  ];
  const otherItems: FeatureFlagItem[] = [
    {
      key: "featureReportingWeeklyActivityEnabled",
      label: t("admin.dataTableAdminTeams.featureFlags.weeklyActivity"),
    },
    {
      key: "featureNewNavigationEnabled",
      label: t("admin.dataTableAdminTeams.featureFlags.newNavigation"),
    },
  ];

  const renderItem = (item: FeatureFlagItem) => {
    const isLockedByContact = item.dependsOnContact && !flags[CONTACT_FLAG];
    const switchId = `${team.id}-${item.key}`;
    return (
      <div
        key={item.key}
        className={cn(
          "flex items-center justify-between gap-4",
          // Elbow connector from the vertical line of the contact group.
          item.dependsOnContact &&
            "relative before:absolute before:-left-4 before:top-1/2 before:h-px before:w-3 before:bg-border"
        )}
      >
        <div className="flex flex-col gap-0.5">
          <Label htmlFor={switchId}>{item.label}</Label>
          {isLockedByContact && (
            <span className="text-xs text-muted-foreground">
              {t("admin.dataTableAdminTeams.featureFlags.requiresContact")}
            </span>
          )}
        </div>
        <Switch
          id={switchId}
          checked={flags[item.key]}
          disabled={pendingFlag !== null || isLockedByContact}
          onCheckedChange={(value) => updateFlag(item.key, value)}
        />
      </div>
    );
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => setOpen(true)}
      >
        <SlidersHorizontalIcon className="size-3.5" />
        <Badge variant="secondary">
          {enabledCount}/{totalCount}
        </Badge>
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("admin.dataTableAdminTeams.featureFlags.title")}
            </DialogTitle>
            <DialogDescription>
              {t("admin.dataTableAdminTeams.featureFlags.description", {
                name: team.name,
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-3">
              {renderItem(contactItem)}
              <div className="ml-4 flex flex-col gap-4 border-l pl-4">
                {contactChannelItems.map(renderItem)}
              </div>
            </div>
            {otherItems.map(renderItem)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
